import Cookies from "js-cookie";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { compose } from "recompose";
import _ from "lodash";
import withLayout from "../../../services/hoc/withLayout";
import { getUserData } from "../../../services/redux/user/reducer";
import { selectUser } from "../../../services/redux/user/selectors";
import PricesPageLayout from "../../averagePrices/PricesPageLayout/PricesPageLayout";
import { Poultry, ProductFamily } from "../../averagePrices/types";
import { Footer } from "../../common";
import {
  addToProfile,
  useDataForRaportCenowy,
  useListOfPoultryProducts,
  useListOfRegions,
} from "./api";
import PriceChart from "./PriceChartSection";
import PriceReport from "./PriceReportSection";
import ReportForm from "./ReportForm";
import avgPricesRoutes from "../../averagePrices/avgPricesRoutes";
import { useAveragePricesHelpers } from "../../averagePrices/helpers";
import ArticlesSectionCarousel from "../../blocks/Section/ArticlesSectionCarousel";
import { getSeoContent } from "./seoContent";

const CATEGORY_FOR_ARTICLES = 28;

const PoultryPrices = ({ currentWidth }) => {
  const token = Cookies.get("user_token");
  const { handleReportTitle, handlePageTitle } = useAveragePricesHelpers();

  function usePrevious(value) {
    const ref = React.useRef();
    React.useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  }
  const { t } = useTranslation(["averagePrices"]);
  const user = useSelector(selectUser);
  const [userDefaults] = user.defaults_markets;
  const [settings, setSettings] = React.useState();
  const prevSettings = usePrevious(settings);

  const { data: poultryProducts } = useListOfPoultryProducts();
  const { data: reportData } = useDataForRaportCenowy(settings);
  const { data: listOfRegions } = useListOfRegions();
  const [isAddedToProfile, setIsAddedToProfile] = React.useState(false);
  const [areUserDefaultsLoaded, setAreUserDefaultsLoaded] = React.useState(
    false
  );
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (!_.isEqual(settings, prevSettings)) setIsAddedToProfile(false);
  }, [settings]);
  const currentSubPath = () => {
    const pathname = window.location.pathname.split("/");
    const subpath = pathname[pathname.length - 1];
    switch (subpath) {
      case "drob":
        return null;
      case "gesi":
        return Poultry.gesi;
      case "indory":
        return Poultry.indory;
      case "indyczki":
        return Poultry.indyczki;
      case "kaczki-brojler":
        return Poultry.kaczki;
      case "kurczeta-brojler":
        return Poultry.kurczeta;
      case "kury-miesne-stada-reprodukcyjne":
        return Poultry.kury;
      default:
        return null;
    }
  };

  const productsInTable =
    currentSubPath() > 0
      ? poultryProducts &&
        poultryProducts.filter(({ id }) => id === currentSubPath())
      : poultryProducts;

  const fetchUser = () => dispatch(getUserData(token));

  React.useEffect(() => {
    if (token) fetchUser();
  }, [token]);

  React.useEffect(() => {
    if (areUserDefaultsLoaded) {
      return;
    }

    if (!poultryProducts || !listOfRegions) {
      return;
    }

    if (userDefaults && userDefaults.product_family === ProductFamily.Poultry) {
      const { regions } = userDefaults;
      setSettings({
        products: productsInTable,
        regions: regions
          .map(id => listOfRegions.find(reg => reg.id === id))
          .filter(Boolean),
      });
      setIsAddedToProfile(true);
      setAreUserDefaultsLoaded(true);
    }
    if (
      settings === undefined &&
      (!userDefaults || userDefaults.product_family !== ProductFamily.Poultry)
    ) {
      setSettings({
        products: productsInTable,
        regions: listOfRegions.map(reg => reg),
      });
    }
  }, [userDefaults, poultryProducts, listOfRegions]);

  const handleAddToProfile = async () => {
    const [marketDefaults] = user.defaults_markets;

    try {
      await addToProfile(user.id, settings, marketDefaults);

      setIsAddedToProfile(true);
      fetchUser(); // update the store
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("COULD NOT SAVE MARKET DEFAULTS");
    }
  };

  if (!poultryProducts || !listOfRegions || !settings) {
    return null;
  }

  return (
    <PricesPageLayout title={handlePageTitle()}>
      <PricesPageLayout.Breadcrumbs
        currentName={t("averagePrices:poultryPage.breadcrumb")}
      />

      <PricesPageLayout.PageTitle>
        {t("averagePrices:mainTitle")} {handleReportTitle()}
      </PricesPageLayout.PageTitle>
      <PricesPageLayout.PageNav
        routes={{
          cereals: avgPricesRoutes.GRAINS.GRAINS_MAIN,
          oleaginous: avgPricesRoutes.OILS.OILS_MAIN,
          poultry: avgPricesRoutes.POULTRY.POULTRY_MAIN,
          pork: avgPricesRoutes.PORK_MAIN,
          dairy: avgPricesRoutes.DAIRY.DAIRY_MAIN,
        }}
        isTopNav
      />
      <PricesPageLayout.PageNav secondary routes={avgPricesRoutes.POULTRY} />
      <PricesPageLayout.FormSection>
        <PricesPageLayout.PriceReportSection>
          <ReportForm
            productsList={poultryProducts}
            listOfRegions={listOfRegions}
            settings={settings}
            setSettings={setSettings}
          />
        </PricesPageLayout.PriceReportSection>
        <PricesPageLayout.PriceReportSection>
          {reportData && (
            <PriceReport
              title={handleReportTitle()}
              weeklyData={reportData.results}
              selectedProducts={productsInTable}
              selectedRegions={settings ? settings.regions : undefined}
            />
          )}
          {!!settings.regions.length && (
            <PriceChart
              title={handleReportTitle()}
              settings={settings}
              currentView={currentSubPath()}
            />
          )}
        </PricesPageLayout.PriceReportSection>
      </PricesPageLayout.FormSection>

      {!token && <PricesPageLayout.PageUnauthorizedInfo />}
      <div className="ArticlesSection">
        <ArticlesSectionCarousel
          title={t("averagePrices:relatedArticles")}
          categoryId={CATEGORY_FOR_ARTICLES}
          showMoreLink={false}
          showCategories={false}
        />
      </div>
      <PricesPageLayout.SeoSection article={getSeoContent(currentSubPath())} />
      <PricesPageLayout.PageFooter routing={avgPricesRoutes} />
      <Footer windowWidth={currentWidth} />
      {token && (
        <PricesPageLayout.PageSubmit
          submitted={isAddedToProfile}
          inactive={!settings.regions.length}
          onClick={() =>
            !!settings.regions.length &&
            !isAddedToProfile &&
            handleAddToProfile()
          }
        >
          {isAddedToProfile
            ? t("averagePrices:formSubmitted")
            : t("averagePrices:submitForm")}
        </PricesPageLayout.PageSubmit>
      )}
    </PricesPageLayout>
  );
};
export default compose(withLayout)(PoultryPrices);
